import type { PlanItem, TaskRow, TaskSuccessor } from '@/api'
import { buildTaskGraph, type TaskGraphEdge, type TaskGraphNode } from '@/lib/taskGraph'

/**
 * What this plan would do to a task on the graph: `new` for a task a `create_task` item adds,
 * `changed` for one an `update_task` item touches, `removed` for one a `delete_task` item removes,
 * and `unchanged` for an existing task drawn only for context (an immediate neighbour of a touched
 * task, or a step the plan's own successors point at).
 */
export type PlanGraphNodeStatus = 'new' | 'changed' | 'removed' | 'unchanged'

/** One task-graph node (see `TaskGraphNode`), tagged with what this plan would do to it. */
export type PlanGraphNode = TaskGraphNode & { status: PlanGraphNodeStatus }

/**
 * What this plan would do to a handoff: `new` for one it adds, `removed` for one it drops, and
 * `unchanged` for one it leaves alone. A handoff can change without either end being created or
 * deleted — an `update_task` item that re-routes an existing task — so this is read from the
 * successor diff first and from the steps at either end second.
 */
export type PlanGraphEdgeStatus = 'new' | 'removed' | 'unchanged'

/** One handoff (see `TaskGraphEdge`), tagged with what this plan would do to it. */
export type PlanGraphEdge = TaskGraphEdge & { status: PlanGraphEdgeStatus }

export type PlanGraph = {
    nodes: PlanGraphNode[]
    edges: PlanGraphEdge[]
}

/**
 * What a create/update/delete verb — an `os_change` item type's prefix — does to its subject. Every
 * item type is `create_`, `update_` or `delete_` of a task, role, team or member, so this reads for
 * any of them alike (used both for the task graph and for a plan item's own change badge).
 */
export function changeVerbOf(type: string): Exclude<PlanGraphNodeStatus, 'unchanged'> {
    const verb = type.split('_')[0]
    if (verb === 'create') return 'new'
    if (verb === 'delete') return 'removed'
    return 'changed'
}

/** The `os_change` item types that move a task on the graph; role/team/member items are listed, not drawn. */
const TASK_ITEM_TYPES = new Set(['create_task', 'update_task', 'delete_task'])

/** True for the item types this graph draws; role/team/member items are listed, not drawn. */
export function isTaskPlanItem(item: PlanItem): boolean {
    return TASK_ITEM_TYPES.has(item.type)
}

/** What a plan item would do to the task it names, or null when it names no task at all. */
export function planItemStatus(item: PlanItem): PlanGraphNodeStatus | null {
    return isTaskPlanItem(item) ? changeVerbOf(item.type) : null
}

/** True when this item's status is for a task that already exists (as opposed to one it creates). */
export function touchesExistingTask(item: PlanItem): boolean {
    const status = planItemStatus(item)
    return status === 'changed' || status === 'removed'
}

export function str(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null
}

/** True when `task` is the task an `update_task`/`delete_task` item names: by id first, then by slug. */
export function matchesPlanTarget(item: PlanItem, task: TaskRow): boolean {
    const taskId = str(item.taskId)
    const slug = str(item.slug)
    return (taskId !== null && task.id === taskId) || (slug !== null && task.slug === slug)
}

/**
 * A plan item's proposed successors. `os_change` payloads carry them the same shape a task version
 * does (`{ slug, on, when }`), so an item that names them replaces the task's current handoffs.
 */
function successorsOf(item: PlanItem): TaskSuccessor[] | null {
    if (!Array.isArray(item.successors)) return null
    const successors: TaskSuccessor[] = []
    for (const entry of item.successors) {
        if (!entry || typeof entry !== 'object') continue
        const candidate = entry as Record<string, unknown>
        const slug = str(candidate.slug)
        if (!slug) continue
        successors.push({
            slug,
            ...(str(candidate.on) ? { on: str(candidate.on) as string } : {}),
            ...(str(candidate.when) ? { when: str(candidate.when) as string } : {}),
        } as TaskSuccessor)
    }
    return successors
}

/** The task an `update_task`/`delete_task` item points at: by id first, then by slug. */
function targetOf(item: PlanItem, tasks: TaskRow[]): TaskRow | undefined {
    return tasks.find(task => matchesPlanTarget(item, task))
}

/** The task a `create_task` item would add, as a row the current-graph builder can lay out. */
function createdTask(item: PlanItem): TaskRow | null {
    const slug = str(item.slug) ?? str(item.handle)
    if (!slug) return null
    return {
        // Empty id: this task does not exist yet, so its card opens nothing.
        id: '',
        slug,
        name: str(item.name) ?? slug,
        kind: str(item.kind) ?? 'llm',
        // A task runs as a role, named by slug or by the handle of a role this plan creates.
        assigneeRole: str(item.assigneeRole) ?? str(item.assigneeRoleHandle),
        currentVersion: {
            goal: str(item.goal) ?? '',
            successors: successorsOf(item),
        },
    }
}

/** An existing task as an `update_task` item would leave it: renamed, re-goaled, re-routed. */
function updatedTask(task: TaskRow, item: PlanItem, successors: TaskSuccessor[] | null): TaskRow {
    return {
        ...task,
        name: str(item.name) ?? task.name,
        currentVersion: {
            ...(task.currentVersion ?? {}),
            goal: str(item.goal) ?? task.currentVersion?.goal ?? '',
            ...(successors ? { successors } : {}),
        },
    }
}

/**
 * One handoff, keyed by the pair it connects and the outcome it fires on — the same identity
 * `buildTaskGraph` draws an edge for, so a diffed handoff can be matched back to its edge.
 */
function handoffKey(source: string, target: string, on: string | null | undefined): string {
    return [source, target, on ?? ''].join('\u0000')
}

/**
 * What an `update_task` item does to a task's handoffs. An item that names `successors` replaces
 * the task's current ones, so a re-routed step adds one handoff and drops another with neither end
 * created or deleted — the case reading the graph off node status alone cannot see.
 *
 * `successors` is what the canvas should draw: the proposed handoffs plus the dropped ones, which
 * stay on the picture (marked `removed`) instead of silently disappearing from it.
 */
function diffHandoffs(
    task: TaskRow,
    proposed: TaskSuccessor[],
): { successors: TaskSuccessor[]; added: string[]; removed: string[] } {
    const current = task.currentVersion?.successors ?? []
    const keyOf = (successor: TaskSuccessor) => handoffKey(task.slug, successor.slug, successor.on)
    const proposedKeys = new Set(proposed.map(keyOf))
    const currentKeys = new Set(current.map(keyOf))
    const dropped = current.filter(successor => !proposedKeys.has(keyOf(successor)))
    return {
        successors: [...proposed, ...dropped],
        added: proposed.map(keyOf).filter(key => !currentKeys.has(key)),
        removed: dropped.map(keyOf),
    }
}

/**
 * Merges an `os_change` plan's items with the current task graph, tagging every node `new`,
 * `changed`, `removed` or `unchanged` (see `PlanGraphNodeStatus`) and drawing the handoffs between
 * them. `tasks` is the plan's own scope: the tasks it creates or touches; `relatedTasks` are their
 * immediate current neighbours, drawn off-canvas for context the same way `buildTaskGraph` already
 * scopes a team or role's workflow (the CEO's answer on #227) — not the whole enterprise.
 */
export function buildPlanGraph(items: PlanItem[], tasks: TaskRow[], relatedTasks: TaskRow[] = []): PlanGraph {
    const status = new Map<string, PlanGraphNodeStatus>()
    const proposed: TaskRow[] = []
    // A task the plan touches can already be in scope or only known as a neighbour; either way the
    // plan's own version of it is what the graph draws.
    const known = [...tasks, ...relatedTasks]
    const replaced = new Map<string, TaskRow>()
    // Handoffs this plan adds or drops without touching the steps at either end (see `diffHandoffs`).
    const addedHandoffs = new Set<string>()
    const droppedHandoffs = new Set<string>()

    for (const item of items) {
        const itemStatus = planItemStatus(item)
        if (!itemStatus) continue
        if (itemStatus === 'new') {
            const created = createdTask(item)
            if (!created) continue
            status.set(created.slug, 'new')
            proposed.push(created)
            continue
        }
        const target = targetOf(item, known)
        if (!target) continue
        status.set(target.slug, itemStatus)
        // A removed task keeps its current shape so the handoffs into it still read in context.
        if (itemStatus !== 'changed') continue
        const proposedSuccessors = successorsOf(item)
        const diff = proposedSuccessors ? diffHandoffs(target, proposedSuccessors) : null
        for (const key of diff?.added ?? []) addedHandoffs.add(key)
        for (const key of diff?.removed ?? []) droppedHandoffs.add(key)
        replaced.set(target.slug, updatedTask(target, item, diff?.successors ?? null))
    }

    const scoped = tasks.map(task => replaced.get(task.slug) ?? task)
    const graph = buildTaskGraph([...scoped, ...proposed], {
        relatedTasks: relatedTasks.map(task => replaced.get(task.slug) ?? task),
    })

    /** True when either end of a handoff is a step carrying the given status. */
    const endpointIs = (edge: TaskGraphEdge, node: PlanGraphNodeStatus) =>
        status.get(edge.source) === node || status.get(edge.target) === node

    /**
     * A handoff reads as whatever the strongest thing about it is: gone, then proposed. A handoff
     * the plan drops or adds outright says so even when both steps it connects stay as they are.
     */
    function edgeStatus(edge: TaskGraphEdge): PlanGraphEdgeStatus {
        const key = handoffKey(edge.source, edge.target, edge.on)
        if (droppedHandoffs.has(key) || endpointIs(edge, 'removed')) return 'removed'
        if (addedHandoffs.has(key) || endpointIs(edge, 'new')) return 'new'
        return 'unchanged'
    }

    return {
        nodes: graph.nodes.map(node => ({
            ...node,
            // A created task has no id to open yet; `buildTaskGraph` copies the placeholder through.
            taskId: node.taskId || null,
            status: status.get(node.slug) ?? 'unchanged',
        })),
        edges: graph.edges.map(edge => ({ ...edge, status: edgeStatus(edge) })),
    }
}
